<template>
  <!-- C-L4 (XOL-596 approval lock UX): the status line of an approval section.
       LOCKED: who locked it and when (or the migration wording), with Unlock for
       those who may. UNLOCKED but decided: "Not locked — …" with Lock. Anything else
       (undecided, nothing to say) renders nothing. Presentational; the parent runs the
       commands and passes `busy`. Lock state is TEXT, never colour alone.

       FROZEN (XOL-596 completed steps, Carlos 2026-10-14: no approval locks or unlocks on a
       completed step): the line keeps saying whether the approval is locked, but with no Lock
       and no Unlock, and an unlocked one says "read-only while the step is complete" instead
       of "this decision can still be changed", which is false then.

       Placement (Carlos, 2026-10-14: "Move the lock to the top of the section, aligned
       to the right, just left of the toggle section arrow"):
         'header'  a collapse's header row, right-aligned just left of its arrow. The
                   parent wraps the collapse item and this line in a
                   `.approval-collapse` (position: relative) and the line sits over the
                   header's right side as a SIBLING of the header, never inside its
                   role="button" (no interactive control nests in one);
         'end'     the top of a section with no collapse header, right-aligned;
         'inline'  the old in-flow line (default).
       Clicks and Enter / Space on it never reach an ancestor, so they cannot toggle a
       collapse the line is drawn over. The visible text is shortened at xs; the text
       element (the one `textId` names, so aria-describedby reads it) keeps the FULL
       wording in a visually hidden span and the short text is aria-hidden; `title`
       and the buttons' aria-labels keep the full wording too. -->
  <div
    v-if="locked || showHint"
    class="approval-lock-line"
    :class="placementClass"
    :title="fullText"
    @click.stop
    @keyup.enter.stop
    @keyup.space.stop>

    <template v-if="locked">
      <i class="fa fa-lock" aria-hidden="true"></i>
      <span :id="textId" ref="text" class="approval-lock-line__text" tabindex="-1"><template v-if="compact"><span class="approval-lock-line__sr">{{lockedText}}</span><span aria-hidden="true">{{lockedShort}}</span></template><template v-else>{{lockedText}}</template></span>

      <el-button
        v-if="canUnlock && !editing && !frozen"
        ref="unlock"
        type="text"
        size="mini"
        icon="fa fa-lock-open"
        :aria-label="unlockAria"
        :disabled="busy"
        @click="$emit('unlock')">{{unlockLabel}}</el-button>
      <small v-else-if="canUnlock && editing && !frozen" class="text-muted">{{afterEdit}}</small>
    </template>

    <template v-else>
      <i class="fa fa-lock-open text-muted" aria-hidden="true"></i>
      <span :id="textId" class="text-muted"><template v-if="compact"><span class="approval-lock-line__sr">{{notLockedText}}</span><span aria-hidden="true">{{notLockedShort}}</span></template><template v-else>{{notLockedText}}</template></span>

      <el-button
        v-if="canLock && !frozen"
        ref="lock"
        type="text"
        size="mini"
        icon="fa fa-lock"
        :aria-label="lockAria"
        :disabled="busy"
        @click="$emit('lock')">{{lockLabel}}</el-button>
    </template>

    <cmd-saving :busy="busy" :label="busyLabel" />
  </div>
</template>

<script>
import { Button } from 'element-ui'
import CmdSaving from '@/components/requests/CmdSaving'
import { copyText } from '@/lib/requestSyncCopy'
import { lockedByMigration } from '@/lib/approvalLock'

export default {
  name: 'approval-lock-line',

  components: { ElButton: Button, CmdSaving },

  props: {
    // The step row (`approvalLocked`, `approvalLocked_by`, `approvalLocked_at`).
    row: {
      type: Object,
      required: true
    },
    // The step's name, for the buttons' aria-labels.
    title: {
      type: String,
      default: ''
    },
    // Is there a decision to lock? (From the baseline; the parent decides.)
    decided: {
      type: Boolean,
      default: false
    },
    canLock: {
      type: Boolean,
      default: false
    },
    canUnlock: {
      type: Boolean,
      default: false
    },
    // Show the "Not locked" hint (and Lock) while unlocked and decided.
    showUnlocked: {
      type: Boolean,
      default: true
    },
    // A lock / unlock command is running for this row.
    busy: {
      type: Boolean,
      default: false
    },
    busyLabel: {
      type: String,
      default: null
    },
    // PRG: the form is in edit mode; Unlock waits for Save or Cancel.
    editing: {
      type: Boolean,
      default: false
    },
    // An id for the text, so a control can point at it with aria-describedby.
    textId: {
      type: String,
      default: null
    },
    // Formatter for the lock moment ("13 Oct 2026 10:47 pm").
    format: {
      type: Function,
      default: null
    },
    // Where the line sits: 'header' | 'end' | 'inline' (see the comment on top).
    placement: {
      type: String,
      default: 'inline'
    },
    // The step is completed (read-only for everyone until it is re-opened): the wording
    // changes, and no Lock / Unlock renders whatever canLock / canUnlock say.
    frozen: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      unlockLabel: copyText('requestSync.approvalLock.line.unlock'),
      lockLabel: copyText('requestSync.approvalLock.line.lock'),
      afterEdit: copyText('requestSync.approvalLock.line.unlockAfterEdit'),
      // xs: the visible text is the short one. Plain data, set from matchMedia (jsdom has none).
      compact: false,
      mql: null
    }
  },

  created () {
    var self = this

    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.mql = window.matchMedia('(max-width: 575px)')
      this.compact = !!this.mql.matches
      this.onMedia = function (e) {
        self.compact = !!e.matches
      }

      if (typeof this.mql.addEventListener === 'function') {
        this.mql.addEventListener('change', this.onMedia)
      } else if (typeof this.mql.addListener === 'function') {
        this.mql.addListener(this.onMedia)
      }
    }
  },

  mounted () {
    this.watchHeader()
  },

  updated () {
    this.watchHeader()
    this.placeUnderHeader()
  },

  beforeDestroy () {
    this.unwatchHeader()

    if (this.mql && this.onMedia) {
      if (typeof this.mql.removeEventListener === 'function') {
        this.mql.removeEventListener('change', this.onMedia)
      } else if (typeof this.mql.removeListener === 'function') {
        this.mql.removeListener(this.onMedia)
      }
    }
  },

  computed: {
    locked () {
      return !!this.row.approvalLocked
    },

    // The wording follows the step: on a completed step the trailing " ·" is dropped
    // (it separated the text from a button that no longer renders) and an unlocked
    // approval says it is read-only, not that it can still be changed.
    notLockedText () {
      return copyText(this.frozen ? 'requestSync.approvalLock.line.notLockedFrozen' : 'requestSync.approvalLock.line.notLocked')
    },

    lockedShort () {
      return copyText(this.frozen ? 'requestSync.approvalLock.line.lockedShortFrozen' : 'requestSync.approvalLock.line.lockedShort')
    },

    notLockedShort () {
      return copyText(this.frozen ? 'requestSync.approvalLock.line.notLockedShortFrozen' : 'requestSync.approvalLock.line.notLockedShort')
    },

    placementClass () {
      return this.placement === 'header' ? 'approval-lock-line--header' : (this.placement === 'end' ? 'approval-lock-line--end' : null)
    },

    // The full wording, always in the title (the visible text is shortened at xs).
    fullText () {
      return this.locked ? this.lockedText : (this.showHint ? this.notLockedText : null)
    },

    showHint () {
      return !this.locked && this.decided && this.showUnlocked
    },

    // Never prints a migration lock's by / at (its time is the deploy time).
    lockedText () {
      if (lockedByMigration(this.row)) {
        return copyText('requestSync.approvalLock.line.lockedMigration')
      }

      var at = this.row.approvalLocked_at
      var datetime = at && this.format ? this.format(at) : null

      return copyText('requestSync.approvalLock.line.lockedBy', { by: this.row.approvalLocked_by, datetime: datetime })
    },

    unlockAria () {
      return copyText('requestSync.approvalLock.line.unlockAria', { title: this.title })
    },

    lockAria () {
      return copyText('requestSync.approvalLock.line.lockAria', { title: this.title })
    }
  },

  methods: {
    // The header placement below 1200px: the line sits UNDER the header text, in the
    // header's extra bottom padding. The header's height depends on how its title wraps
    // (a 320px phone wraps "Approved ✓ on 30 Sep 2026"), so the line's `top` follows it:
    // a fixed offset overlapped a wrapped title. Above 1200px (CSS gives `top: 0`) the
    // inline top is cleared.
    placeUnderHeader () {
      var el = this.$el
      var header = this.headerEl

      if (this.placement !== 'header' || !el || el.nodeType !== 1 || !header) {
        return
      }

      var narrow = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 1199px)').matches

      el.style.top = narrow && header.offsetHeight ? (header.offsetHeight - 30) + 'px' : ''
    },

    // Find the collapse header the line is drawn over (a sibling of the collapse item in
    // the `.approval-collapse` wrapper) and follow its size.
    watchHeader () {
      var el = this.$el
      var wrap = el && el.nodeType === 1 ? el.parentElement : null
      var header = wrap && this.placement === 'header' ? wrap.querySelector('.el-collapse-item__header') : null

      if (header === this.headerEl) {
        return
      }

      this.unwatchHeader()
      this.headerEl = header

      if (!header) {
        return
      }

      var self = this

      this.onResize = function () {
        self.placeUnderHeader()
      }

      if (typeof window.ResizeObserver === 'function') {
        this.observer = new window.ResizeObserver(this.onResize)
        this.observer.observe(header)
      }

      window.addEventListener('resize', this.onResize)
      this.placeUnderHeader()
    },

    unwatchHeader () {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }

      if (this.onResize && typeof window !== 'undefined') {
        window.removeEventListener('resize', this.onResize)
      }

      this.headerEl = null
      this.onResize = null
    },

    // After a command: put focus where the user's attention should go.
    focusUnlock () {
      return this.focusRef('unlock') || this.focusRef('text')
    },

    // After an Unlock: the hint's Lock when it renders; when it does not (this user may
    // not lock), the first enabled approval control, so focus never falls to <body>
    // (C-L4). "Approval control" = the first enabled field or button of the section the
    // line sits in, outside the line itself.
    focusLock () {
      return this.focusRef('lock') || this.focusFirstControl()
    },

    focusFirstControl () {
      var line = this.$el
      var section = line && line.parentElement ? line.parentElement : null

      if (!section) {
        return false
      }

      // The line's own parent can be a bare column (the PRG forms): widen to the form.
      var form = typeof section.closest === 'function' ? section.closest('form, .el-form') : null
      var scope = section.querySelector('input:not([disabled]), button:not([disabled])') ? section : (form || section)
      var found = scope.querySelectorAll('input:not([disabled]):not([type=hidden]), button:not([disabled]), textarea:not([disabled])')

      for (var i = 0; i < found.length; i++) {
        if (!line.contains(found[i])) {
          found[i].focus()
          return true
        }
      }

      return false
    },

    focusText () {
      this.focusRef('text')
    },

    focusRef (name) {
      var ref = this.$refs[name]
      var el = ref ? (ref.$el || ref) : null

      if (el && typeof el.focus === 'function') {
        el.focus()
        return true
      }

      return false
    }
  }
}
</script>

<style scoped>
.approval-lock-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin: 6px 0;
  font-size: 13px;
  line-height: 1.5;
}

.approval-lock-line .fa-lock {
  color: #606266;
}

.approval-lock-line__text {
  color: #606266;
}

/* Visually hidden, still read: the full wording behind the short text at xs (the repo
   has no global .sr-only; same rule as BackhaulCustomer). */
.approval-lock-line__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* A section with no collapse header: the line sits at the top, right-aligned. */
.approval-lock-line--end {
  justify-content: flex-end;
  margin: 0 0 6px;
}

/* The header placement: small and muted, one line, vertically centred on the 48px
   header row, just left of the toggle arrow (the arrow's own 8px margin plus its icon).
   The wrapper (.approval-collapse, below) is the positioning context. */
.approval-lock-line--header {
  position: absolute;
  top: 0;
  right: 40px;
  z-index: 2;
  height: 48px;
  margin: 0;
  flex-wrap: nowrap;
  white-space: nowrap;
  font-size: 12px;
  font-weight: normal;
  line-height: 1.5;
}

.approval-lock-line--header .approval-lock-line__text {
  color: #909399;
}

/* The command cue ("Locking…" / "Unlocking…") grows to the LEFT of the text, so the
   Lock / Unlock button next to the arrow never moves. */
.approval-lock-line--header ::v-deep .cmd-saving {
  order: -1;
  min-width: 0;
}
</style>

<style>
/* The collapse item and its header-placed line. Not scoped: the wrapper and the
   collapse's own header are the parent's and Element's elements. */
.approval-collapse {
  position: relative;
}

/* Below 1200px the header may not fit the status text AND the line: the line drops under the
   header text, right-aligned, inside extra header padding. It never overlaps the arrow
   (the header row's right 40px stay clear) or the status text. */
@media (max-width: 1199px) {
  .approval-collapse--line > .el-collapse-item > div > .el-collapse-item__header {
    height: auto;
    min-height: 48px;
    padding-bottom: 30px;
  }

  /* `top` is set inline from the header's height (ApprovalLockLine.placeUnderHeader). */
  .approval-collapse--line > .approval-lock-line--header {
    top: 36px;
    right: 12px;
    height: 28px;
    max-width: calc(100% - 24px);
    white-space: normal;
    justify-content: flex-end;
  }
}
</style>
